<template>
    <div class="flex items-center">
        <!-- Workspaces come from users/me, which only loads in the browser, so the menu never takes part in hydration -->
        <ClientOnly v-if="isAuthenticated">
            <PopoverWrapper v-if="isDesktop" ref="popover" placement="bottom-end" :offset="{ crossAxis: 0, mainAxis: 8 }">
                <PopoverButton>
                    <WorkspacePill :shortcut-label="shortcutLabel" />
                </PopoverButton>
                <PopoverDialog class="w-[340px] max-w-[calc(100vw-2rem)] rounded-3xl bg-system-light shadow-xl">
                    <li class="list-none">
                        <WorkspaceMenu @close="closeMenu" />
                    </li>
                </PopoverDialog>
            </PopoverWrapper>
            <template v-else>
                <WorkspacePill :shortcut-label="shortcutLabel" @click="isSheetOpen = true" />
                <ActionSheet v-model:open="isSheetOpen" variant="light" :padding="false">
                    <WorkspaceMenu @close="closeMenu" />
                </ActionSheet>
            </template>
        </ClientOnly>
        <Button v-else variant="light" size="md" icon="user" label="Sign in" class="shadow-sm" @click="goToSignIn" />
    </div>
</template>

<script setup lang="ts">
    import { useEventListener, useMediaQuery } from '@vueuse/core'
    import { ActionSheet, Button, PopoverButton, PopoverDialog, PopoverWrapper } from '@tapp.online/ui'
    import WorkspacePill from '@/components/workspace/Pill.vue'
    import WorkspaceMenu from '@/components/workspace/Menu.vue'

    const route = useRoute()
    const { isAuthenticated } = useAuth()
    const { signInPath, redirectQueryKey } = useAuthConfig()

    // The sm breakpoint: an action sheet on phones, a popover under the pill above that
    const isDesktop = useMediaQuery('(min-width: 40rem)')

    const popover = ref<InstanceType<typeof PopoverWrapper> | null>(null)
    const isSheetOpen = ref(false)

    const closeMenu = () => {
        popover.value?.closePopover()
        isSheetOpen.value = false
    }

    // Crossing the breakpoint swaps the popover for the sheet or back, so an open menu reopens in the other form.
    // Runs before the swap renders, while the leaving popover can still say whether it was open.
    watch(isDesktop, async (toDesktop) => {
        const wasOpen = toDesktop ? isSheetOpen.value : !!popover.value?.showPopover
        isSheetOpen.value = !toDesktop && wasOpen
        if (!toDesktop || !wasOpen) return
        await nextTick()
        if (!popover.value?.showPopover) popover.value?.togglePopover()
    })

    // The report URL carries the token, so sending it along as the redirect brings the user straight back after signing in
    const goToSignIn = () => navigateTo({ path: signInPath, query: { [redirectQueryKey]: route.fullPath } })

    const shortcutLabel = ref('⌘K')
    onMounted(() => {
        if (!/mac/i.test(navigator.userAgent)) shortcutLabel.value = 'Ctrl+K'
    })

    // ⌘K (Ctrl+K on Windows/Linux) toggles the menu from anywhere, as in the dashboard
    useEventListener(document, 'keydown', (event: KeyboardEvent) => {
        if (!isAuthenticated.value || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return
        event.preventDefault()
        if (isDesktop.value) popover.value?.togglePopover()
        else isSheetOpen.value = !isSheetOpen.value
    })
</script>
